import React, { useEffect, useRef, useState } from "react";
import "./paymentSuccess.css";
import "../checkoutFooter/bottomFooter.css";
import { Button, Divider, Skeleton, Typography } from "antd";
import Lottie from "lottie-react";
import animationData from "../lotties/successLogo.json";
import paymentExpired from "../lotties/paymentExpired.json";
import { useDispatch, useSelector } from "react-redux";
import { clearGuestCheckout, downloadElementAsPdf, generateDynamicStyling, getNewUrlWithAllQueryParams, getStorage, isEmpty, numberToCurrency } from "../../../Utils/utils";
import { getTransactionStatus, selectTransactionData } from "../../../slices/TransactionIdSlice";
import TranslateText from "../../resuableComponents/TranslateText";
import { selectFrontendBackUrl, selectFrontReturnUrl } from "../../../slices/PaymentSessionDetailsSlice";
import { RETRY_LIMIT_KEYS } from "../../../slices/PaymentInitiationSlice";
import { useTranslation } from "react-i18next";
import { CloseCircleFilled, DoubleRightOutlined, DownloadOutlined } from "@ant-design/icons";
import { setOnDemandAmount, setOnDemandLoaded } from "../../../slices/CommonPersistedSlice";
import dateFormat from "dateformat";
import { handleMerchantLogo } from "../../../slices/HandleAdditionalComponentsSlice";

// checkout language code -> BCP 47 locale used to format the transaction date (English keeps dateformat)
const DATE_LOCALES = { hn: "hi", mr: "mr", ta: "ta", zh: "zh", vi: "vi", id: "id", ko: "ko", es: "es" };

// isFailed: the same page as a payment failed page (order / session retry limit reached).
const PaymentSuccess = ({ isFailed = false }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { isDccQuotationSelected: isDccSelectedInStore } = useSelector((state) => state.cardDetails);
  const isDccQuotationSelected = isDccSelectedInStore && !isFailed;
  const { dccQuotation, cardHolderName, dccCardScheme } = useSelector(state => state.cardDetails);
  const { brand, baseMoney } = dccQuotation;
  const merchantCurrencyCode = baseMoney?.currencyCode;
  const merchantCurrencyAmount = baseMoney?.amount;
  const { dccMoney, fxRate, marginPercent } = dccQuotation?.dccQuotationDetails || {};
  const issuerCurrencyCode = dccMoney?.currencyCode;
  const issuerCurrencyAmount = dccMoney?.amount;
  const paymentSuccessData = useSelector((state) => selectTransactionData(state));
  const sessionDetails = getStorage("session", "s_d");
  const transactionId = getStorage("session", "t_id") || getStorage("session", "s_d")?.lastTransactionId;
  const token = getStorage("session", "tkn");
  const [paymentSuccessDetails, setPaymentSuccessDetails] = useState(null);
  const [showOnlyText, setShowOnlyText] = useState();
  const frontReturnUrl = useSelector((state) => selectFrontReturnUrl(state));
  const [viewMore, setViewMore] = useState(false)
  const [orderDetailObj, setOrderDetailObj] = useState([]);
  const { order } = sessionDetails?.paymentDetails || {};
  const orderId = sessionDetails?.paymentDetails?.context?.orderId;
  const frontendReturnUrl = useSelector((state) => selectFrontReturnUrl(state));
  const frontendBackUrl = useSelector((state) => selectFrontendBackUrl(state));
  // Success goes back to the merchant's return url, failed to the back url.
  const redirectUrl = isFailed ? frontendBackUrl : frontendReturnUrl;
  const { retryLimitType } = useSelector((state) => state.paymentInitiation);
  // After a payment attempt the limit type is in redux; when the get session API says all attempts
  // are used up (paymentAttemptsExhausted) the page opens fresh, so it comes from the stored session.
  const retryLimitKey = !isFailed ? "" : (retryLimitType === "order" || retryLimitType === "session")
    ? RETRY_LIMIT_KEYS[retryLimitType]
    : sessionDetails?.paymentAttemptsExhausted ? RETRY_LIMIT_KEYS.session : "";
  const redirectionDelayIntervalRef = useRef(null);
  const redirectTimeoutRef = useRef(null);
  const [redirectDelay, setRedirectDelay] = useState(5);
  const receiptRef = useRef(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  const handleDownloadReceipt = () => {
    if (!receiptRef.current || downloadingReceipt) return;
    setDownloadingReceipt(true);
    // logo is hidden on screen; reveal it only in the copy that is exported
    const receiptClone = receiptRef.current.cloneNode(true);
    const logo = receiptClone.querySelector(".domestic-s-receipt-logo");
    if (logo) logo.style.display = "flex";
    downloadElementAsPdf(receiptClone, {
      filename: `receipt-${paymentSuccessData?.transactionId || transactionId || "boxpay"}`,
    }).finally(() => setDownloadingReceipt(false));
  };

  useEffect(() => {
    if (!isEmpty(sessionDetails) && !isEmpty(redirectUrl)) {
      const returnUrlWithParam = isFailed ? redirectUrl : getNewUrlWithAllQueryParams(redirectUrl, null, 'token');

      setRedirectDelay(5);
      redirectionDelayIntervalRef.current = setInterval(() => {
        setRedirectDelay((prev) => {
          if (prev <= 1) {
            clearInterval(redirectionDelayIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      redirectTimeoutRef.current = setTimeout(() => {
        if (!returnUrlWithParam?.includes("finalStatus")) {
          window.location.href = returnUrlWithParam;
        }
      }, 5000);
    }

    return () => {
      clearInterval(redirectionDelayIntervalRef.current);
      clearTimeout(redirectTimeoutRef.current);
      redirectionDelayIntervalRef.current = null;
      redirectTimeoutRef.current = null;
    };
  }, [redirectUrl])

  generateDynamicStyling(sessionDetails);

  useEffect(() => {
    if (token && transactionId) {
      dispatch(getTransactionStatus({ token, transactionId }));
      setShowOnlyText(false);
    } else {
      // A failed page has no transaction when the limit is hit before any payment: still show it.
      setShowOnlyText(!isFailed);
    }
  }, [token, transactionId]);

  const getTranslatedStatus = (status) => {
    const key = `STATUS_${status?.toUpperCase()}`;
    return status && i18n.exists(key) ? t(key) : status;
  };

  const formatTransactionDate = (timestamp) => {
    const dateLocale = DATE_LOCALES[i18n.language];
    if (dateLocale && timestamp) {
      return new Intl.DateTimeFormat(dateLocale, {
        dateStyle: "full",
        timeStyle: "medium",
      }).format(new Date(timestamp));
    }
    return dateFormat(timestamp, "dddd, mmmm dS, yyyy, h:MM:ss TT");
  };

  const setPaymentDictionary = () => {
    // Hide the merchant-added "Taxes & Fees" line item from the success-page product
    // list (only that item; everything else is shown as-is).
    const visibleOrderItems = order?.items?.filter(
      (item) => item?.itemName?.trim().toLowerCase() !== "taxes & fees"
    );
    let paymentSummaryDictionary = [
      {
        title: t("TRANSACTION_ID"),
        value: paymentSuccessData?.transactionId,
        show: !isFailed || !!paymentSuccessData?.transactionId
      },
      {
        title: t("ORDER_ID"),
        value: orderId,
        show: !!orderId
      },
      {
        title: t("CARD_TYPE"),
        value: brand,
        show: isDccQuotationSelected
      },
      {
        title: t("CARDHOLDER_NAME"),
        value: cardHolderName,
        show: isDccQuotationSelected
      },
      {
        title: `${t("TRANSACTION_TOTAL")} ${merchantCurrencyCode}`,
        value: `${merchantCurrencyCode} ${merchantCurrencyAmount}`,
        show: isDccQuotationSelected
      },
      {
        title: t("EXCHANGE_RATE"),
        value: `1 ${merchantCurrencyCode} = ${fxRate?.toFixed(2)} ${issuerCurrencyCode}`,
        show: isDccQuotationSelected
      },
      {
        title: t("MARGIN"),
        value: marginPercent + "%",
        show: isDccQuotationSelected && dccCardScheme === 'VISA'
      },
      {
        title: t("TRANSACTION_CURRENCY"),
        value: issuerCurrencyCode,
        show: isDccQuotationSelected
      },
      {
        title: t("STATUS"),
        value: getTranslatedStatus(isFailed ? "FAILED" : paymentSuccessData?.status),
        show: true
      },
      {
        title: t("DATE_AND_TIME"),
        value: formatTransactionDate(paymentSuccessData?.transactionTimestamp),
        show: !isFailed || !!paymentSuccessData?.transactionTimestamp
      },
      {
        title: t("PRODUCT_NAME"),
        value: visibleOrderItems?.length ? visibleOrderItems : sessionDetails?.paymentDetails?.product?.name,
        type: visibleOrderItems?.length ? 'array' : null,
        show: !isFailed && (!isEmpty(order) || sessionDetails?.paymentDetails?.product?.name)
      },

      // {
      //   title: t("CONFIRMATION_MESSAGE"),
      //   value: paymentSuccessData?.paymentConfirmationMessage,
      //   show: true
      // },
    ];
    setPaymentSuccessDetails(paymentSummaryDictionary);
  }

  const setOrderDetails = () => {
    const orderDetails = [
      // {
      //   label: "SUBTOTAL",
      //   title: "Subtotal",
      //   value: paymentSuccessData?.order?.originalAmount || paymentSuccessData?.originalAuthAmount,
      //   show: true
      // },
      {
        label: "SHIPPING_COST",
        title: "Shipping Cost",
        value: paymentSuccessData?.order?.shippingAmount || 0,
        show: true
      },
      {
        label: "CONVENIENCE_FEE",
        title: "Convenience Fee",
        value: paymentSuccessData?.additionalChargeAmount || 0,
        show: true
      },
      {
        label: "TAXES",
        title: "Taxes",
        value: paymentSuccessData?.order?.taxAmount || 0,
        show: true
      },
      // {
      //   label: "INTEREST_CHARGED_BY_BANK",
      //   title: "Interest Charged By Bank",
      //   value: paymentSuccessData?.emiMethod?.bankChargedInterestAmount || 0,
      //   show: !paymentSuccessData?.paymentMethod?.type?.toLowerCase() === 'emi'
      // },
      // {
      //   label: "DISCOUNT",
      //   title: "Discount",
      //   value: paymentSuccessData?.order?.totalDiscountedAmount || 0,
      //   show: true,
      //   style: { color: '#2DA94F' }
      // },
      // {
      //   label: paymentSuccessData?.emiMethod?.applicableOffer?.discount?.type === 'NoCostEmi' ? "NO_COST_EMI_DISCOUNT" : "LOW_COST_EMI_DISCOUNT",
      //   title: paymentSuccessData?.emiMethod?.applicableOffer?.discount?.type === 'NoCostEmi' ? "No Cost EMI Discount" : "Low Cost EMI Discount",
      //   value: paymentSuccessData?.emiMethod?.merchantBorneInterestAmount || 0,
      //   show: !paymentSuccessData?.paymentMethod?.type?.toLowerCase() === 'emi',
      //   style: { color: '#2DA94F' }
      // },
    ];
    setOrderDetailObj(orderDetails);
  }
  useEffect(() => {
    if (paymentSuccessData || isFailed) {
      if (!isFailed) localStorage.removeItem("previousActivePaymentTab");
      setPaymentDictionary();
      setOrderDetails();
    }
  }, [paymentSuccessData]);
  useEffect(() => {
    const handleLanguageChange = () => {
      setPaymentDictionary();
      setOrderDetails()
    };

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, paymentSuccessData]);
  const DccAmount = () => {
    return (
      <div className="domestic-s-bold-text">
        <span>{issuerCurrencyCode} </span>
        <span>{issuerCurrencyAmount}</span>
      </div>
    )
  }
  const currencyCode = sessionDetails?.paymentDetails?.money?.currencyCode || paymentSuccessData?.currencyCode
  const getAmount = () => (paymentSuccessData?.paymentMethod?.type?.toLowerCase() === 'emi' && paymentSuccessData?.emiMethod?.netAmount)
    ? paymentSuccessData?.emiMethod?.netAmount : paymentSuccessData?.amount;

  useEffect(() => {
    if (isFailed) return
    dispatch(setOnDemandAmount(null))
    dispatch(setOnDemandLoaded(false))
    // Payment done — clear the persisted guest checkout.
    clearGuestCheckout()
  }, [])


  useEffect(() => {
    if (isEmpty(sessionDetails?.configs?.additionalFieldSets)) return
    dispatch(handleMerchantLogo(sessionDetails?.configs?.additionalFieldSets.includes("MERCHANT_LOGO")));

  }, [sessionDetails])
  return (
    <div style={{ minHeight: "calc(100vh - 72px)", display: "flex" }}>
      {showOnlyText === undefined ? (
        <></>
      ) : (
        <>
          {
            !showOnlyText ? (
              <div className="domestic-s-root dynamic-font-styling" ref={receiptRef}>
                <div className="domestic-s-header">
                  {isFailed && <CloseCircleFilled className="payment-failed-icon" />}
                  {!isFailed && paymentSuccessData?.status && <Lottie
                    animationData={paymentSuccessData?.status?.toLowerCase() === 'expired' ? paymentExpired : animationData}
                    loop={true}
                    autoplay={true}
                    style={{ width: "70px", height: "70px" }}
                    speed={4.5}
                  />}

                  <Typography
                    className="semiBold24Heading dynamic-font-styling"
                    style={{
                      padding: showOnlyText && "0px 40px",
                      textAlign: "center",
                    }}
                  >
                    {
                      isFailed ? <>
                        <TranslateText label={"PAYMENT_FAILED"}>Payment failed</TranslateText>!
                      </> : paymentSuccessData?.status?.toLowerCase() === 'expired' ? <>
                        <TranslateText label={"PAYMENT_EXPIRED"}>Payment Expired</TranslateText>!
                      </> :
                        <>

                          {showOnlyText
                            ? <TranslateText label={"PAYMENT_ALREADY_COMPLETED"}>Payment has already been completed on this link</TranslateText>
                            : (
                              <>
                                <TranslateText label={"PAYMENT_SUCCESSFUL"}>Payment Successful</TranslateText>!
                                {
                                  isDccQuotationSelected && (<DccAmount />)
                                }
                              </>
                            )
                          }
                        </>
                    }
                  </Typography>
                  {retryLimitKey && (
                    <Typography className="payment-failed-message dynamic-font-styling">
                      {t(retryLimitKey)}
                    </Typography>
                  )}
                </div>
                <div className="domestic-s-divider-root">
                  <div className="domestic-s-cut-1"></div>
                  <div className="domestic-s-divider"></div>
                  <div className="domestic-s-cut-2"></div>
                </div>
                <div className="domestic-s-summary dynamic-font-styling">
                  {
                    sessionDetails?.merchantDetails?.merchantName && (
                      <div className="domestic-s-summary-item">
                        <Typography className="domestic-s-summary-item-value">
                          <b> <TranslateText label={"YOUR_MERCHANT_NAME"}>Your Merchant name</TranslateText></b>
                        </Typography>
                        <Typography className="domestic-s-summary-item-value">
                          {sessionDetails.merchantDetails.merchantName}
                        </Typography>
                      </div>
                    )
                  }

                  {paymentSuccessDetails ? paymentSuccessDetails?.map((data, i) => {
                    return data.show && (
                      <div className="domestic-s-summary-item" key={`details-${i}`}>
                        <Typography className="domestic-s-summary-item-title">
                          {data?.title}
                        </Typography>
                        <Typography className="domestic-s-summary-item-value">
                          {
                            data?.type === 'array' ? (
                              <>
                                <div className="product-item-container">
                                  {
                                    data.value.map((d, i) => (
                                      <span key={i}
                                        style={{
                                          display: viewMore || i === 0 ? 'block' : 'none',
                                          textAlign: 'right'
                                        }}>
                                        {`${d.itemName} X (x${d.quantity})`}
                                      </span>
                                    ))
                                  }
                                  {
                                    data?.value?.length > 1 && (
                                      <span className="view-text" style={{ color: sessionDetails?.merchantDetails?.checkoutTheme?.headerColor }}
                                        onClick={() => setViewMore(p => !p)}>
                                        <span style={{ paddingRight: '0.4rem' }}>
                                          {viewMore ?
                                            <TranslateText label={"VIEW_LESS_ITEMS"}>View Less Items</TranslateText>
                                            :
                                            <TranslateText label={"VIEW_MORE_ITEMS"}>View More Items</TranslateText>
                                          }
                                        </span>
                                        <span style={{ transform: !viewMore ? 'rotate(90deg)' : 'rotate(270deg)' }}>
                                          <DoubleRightOutlined style={{ fontSize: '12px', color: sessionDetails?.merchantDetails?.checkoutTheme?.headerColor }} />
                                        </span>
                                      </span>
                                    )
                                  }
                                </div>
                              </>
                            ) :
                              <div className="product-item-container">
                                <span style={{ textAlign: 'right' }}>
                                  {data?.value}
                                </span>
                              </div>
                          }
                        </Typography>
                      </div>
                    )
                  }) : [...Array(3)].map((_, i) => (
                    <Skeleton.Button active size="small" block key={i} />
                  ))}

                  {paymentSuccessData?.paymentConfirmationMessage &&
                    <>
                      <div className="domestic-s-divider-2"></div>
                      <Typography className="domestic-s-summary-item-title" style={{ textAlign: "left", textTransform: "capitalize" }}>

                        <TranslateText label={"CONFIRMATION_MESSAGE"}>Payment confirmation message</TranslateText>
                      </Typography>
                      <Typography
                        className="domestic-s-summary-item-value"
                        style={{ whiteSpace: "pre-wrap", textAlign: "left" }}
                      >
                        {paymentSuccessData?.paymentConfirmationMessage}
                      </Typography>
                    </>
                  }
                  <div className="domestic-s-divider-2"></div>
                  {/* {
                    !isEmpty(orderDetailObj)
                    && orderDetailObj.filter((d) => Number(d?.value) > 0).length > 1
                    && orderDetailObj.map((d, i) => {
                      // !isEmpty(order) && !isEmpty(orderDetailObj) && orderDetailObj.map((d, i) => {
                      return (Number(d?.value) > 0) && <div className="domestic-s-summary-item">
                        <Typography className="domestic-s-summary-item-title">
                          <TranslateText label={d.label}>{t(`${d?.title}`)}</TranslateText>
                        </Typography>
                        <Typography className="domestic-s-summary-item-value" style={{ ...d?.style }}>
                          {d?.style && '-'}
                          <span className="currency-symbol" style={{ fontWeight: 500 }}> {currencyCode} </span>{" "}
                          {numberToCurrency(d?.value, paymentSuccessData?.currencyCode)}
                        </Typography>
                      </div>
                    })
                  }
                  {
                    !isEmpty(orderDetailObj) && orderDetailObj.filter((d) => Number(d?.value) > 0).length > 1 && (
                      <div className="domestic-s-divider-2"></div>
                    )
                  } */}
                  {/* {paymentSuccessData?.offerId && <>
                    <div className="domestic-s-summary-item">
                      <Typography className="domestic-s-summary-item-title">
                        <TranslateText label={"SUBTOTAL"} >SubTotal</TranslateText>
                      </Typography>
                      <Typography className="domestic-s-summary-item-value">
                        {paymentSuccessData?.originalAuthAmount}
                      </Typography>
                    </div>
                    <div className="domestic-s-summary-item">
                      <Typography className="domestic-s-summary-item-title">
                        <TranslateText label={"DISCOUNT"} >Discount </TranslateText> ({paymentSuccessData?.offerId}) Applied
                      </Typography>
                      <Typography className="domestic-s-summary-item-value" style={{ color: "#019939" }}>
                        -   <span className="currency-symbol" style={{ fontWeight: 500 }}>{currencyCode}</span> {numberToCurrency(paymentSuccessData?.discountAmount, paymentSuccessData?.currencyCode)}
                      </Typography>

                    </div>


                    <div className="domestic-s-divider-2"></div>
                  </>} */}
                  <div className="domestic-s-summary-item dynamic-font-styling">
                    {paymentSuccessData || isFailed ? (
                      <>
                        <Typography className="domestic-s-summary-item-title">
                          {isFailed
                            ? <TranslateText label={"AMOUNT"}>Amount</TranslateText>
                            : <TranslateText label={"TOTAL_AMOUNT"}>Total Amount Paid</TranslateText>}
                        </Typography>

                        {
                          isDccQuotationSelected ? ((<DccAmount />)) : (
                            <Typography className="domestic-s-amount domestic-s-bold-text">
                              <span className="currency-symbol" style={{ fontWeight: 600 }}>{currencyCode}</span>
                              <span className="domestic-s-amount-value">
                                {isFailed
                                  ? numberToCurrency(sessionDetails?.paymentDetails?.money?.amount, currencyCode)
                                  : numberToCurrency(getAmount(isDccQuotationSelected), paymentSuccessData?.currencyCode)}
                              </span>
                            </Typography>
                          )
                        }
                      </>
                    ) : (
                      <Skeleton.Button active size="small" block />
                    )}
                  </div>
                  {
                    isDccQuotationSelected && (
                      <>
                        <div className="domestic-s-divider-2"></div>
                        <div>
                          {
                            dccCardScheme === 'VISA' && (
                              <Typography className="domestic-s-summary-item-title domestic-s-summary-margin domestic-s-bold-text">
                                <TranslateText label={"CURRENCY_CHOICE_OFFERED"}> I have been offered a choice of currencies and agreed to pay in </TranslateText>
                                <span className="currency-span">{issuerCurrencyCode}.</span>
                                <TranslateText label={"CURRENCY_CONVERSION_SERVICE_PROVIDER"}> This currency conversion service is provided by Fexco</TranslateText>.
                              </Typography>
                            )
                          }

                          <div className="domestic-s-print-text">
                            <Typography className="domestic-s-summary-item-title domestic-s-bold-text">
                              <TranslateText label={"PRINT_AND_RETAIN_RECORDS"}>Please print and retain for your records</TranslateText>
                            </Typography>
                          </div>
                        </div>
                      </>
                    )
                  }

                </div>
                <div className="domestic-s-footer-divider">
                  <Divider />
                </div>

                {/* visible only in the downloaded receipt */}
                <div className="domestic-s-receipt-logo" style={{ display: "none", justifyContent: "center", padding: "12px 0" }}>
                  <div className="boxpay-branding">
                    <img src="/assets/footer/boxpay-footer.svg" alt="boxpay-footer-logo" style={{ height: 18, width: 16 }} />
                    <span className="footer-boxpay-back dynamic-font-family" style={{ fontSize: 16, lineHeight: "24px" }}>BoxPay</span>
                  </div>
                </div>

                {!isFailed && (
                  <div className="domestic-s-download-receipt" data-html2canvas-ignore="true">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      loading={downloadingReceipt}
                      onClick={handleDownloadReceipt}
                      style={{ background: sessionDetails?.merchantDetails?.checkoutTheme?.headerColor }}
                    >
                      <TranslateText label={"DOWNLOAD_RECEIPT"}>Download Receipt</TranslateText>
                    </Button>
                  </div>
                )}

                <div className="domestic-s-footer dynamic-font-styling" data-html2canvas-ignore="true">
                  {
                    isDccQuotationSelected && !isEmpty(frontReturnUrl) ? (
                      <>
                        <Typography className="domestic-s-summary-item-title dynamic-font-styling">
                          <TranslateText label={"REDIRECT_BACK"}>Redirect back to the </TranslateText>
                          <a href={frontReturnUrl} className="domestic-s-merchant-site-link dynamic-title">
                            <TranslateText label={"MERCHANT_SITE"}> merchant's site</TranslateText>
                          </a>
                        </Typography>
                      </>
                    ) : !isEmpty(redirectUrl) && !redirectUrl?.includes('finalStatus') ?
                      <div>
                        <Typography className="domestic-s-summary-item-title dynamic-font-styling">
                          <TranslateText label={"REDIRECTING_TO_MERCHANT_PAGE"}>Redirecting to the merchant page.{" "} </TranslateText>
                          {/* NEED to translate these  */}
                          <TranslateText label={"DO_NOT_CLOSE_WINDOW"}>Please do not close this window. </TranslateText>
                        </Typography>
                        <div className="redirect-counter-div">
                          <TranslateText label={"REDIRECTING_IN"}>Redirecting in</TranslateText> <span className="redirect-counter" style={{ color: sessionDetails?.merchantDetails?.checkoutTheme?.headerColor }}>{redirectDelay}</span> <TranslateText label={"SECONDS"}>seconds</TranslateText>...
                        </div>
                      </div>
                      : (
                        <Typography className="domestic-s-summary-item-title dynamic-font-styling">
                          <TranslateText label={"CLOSE_THE_WINDOW"}>You may close the window.</TranslateText>.
                        </Typography>
                      )
                  }
                </div>
              </div>
            ) : (
              <div
                style={{
                  margin: "auto",
                }}
              >
                <div style={{ width: "258px" }}>
                  <img
                    src="/assets/errorImage/oppsImage.svg"
                    style={{ width: "calc(166 / 161 * 100%)" }}
                  />
                </div>
                <Typography className="oppsHeading">
                  <TranslateText label={"OOPS"}>Oops</TranslateText>!! </Typography>
                <Typography className="oppsSubHeading">
                  <TranslateText label={"SEEMS_LIKE_LOST"}>Seems like you are lost</TranslateText>.
                </Typography>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
